import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { tagAdminService } from "@/features/tags/services/tags-admin.service";
import type { TagResponseDTO } from "@/features/tags";
import { clientAdminService } from "@/features/clients/services/client-admin.service";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import { graduationService } from "@/features/graduations/services/graduation.service";
import { useGraduationList } from "@/features/graduations/hooks/useGraduationList";
import type { GraduationListFilters } from "@/features/graduations/hooks/useGraduationList";
import type {
  GraduationRequestDTO,
  GraduationResponseDTO,
} from "@/features/graduations/types/graduation.types";
import { GraduationsTable } from "@/features/graduations/components/GraduationsTable";
import { GraduationFilters } from "@/features/graduations/components/GraduationFilters";
import { GraduationFormDialog } from "@/features/graduations/components/GraduationFormDialog";
import type { GraduationFormData } from "@/features/graduations/components/GraduationFormDialog";
import { GraduationDeleteDialog } from "@/features/graduations/components/GraduationDeleteDialog";
import { GraduationCancelDialog } from "@/features/graduations/components/GraduationCancelDialog";

const DIALOG_MODE = {
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
  CANCEL: "cancel",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

export function FormaturasPage() {
  const list = useGraduationList();

  const [tagsLookup, setTagsLookup] = useState<Map<number, TagResponseDTO>>(
    () => new Map(),
  );
  const [clientsLookup, setClientsLookup] = useState<
    Map<number, ClientResponseDTO>
  >(() => new Map());

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selectedGraduation, setSelectedGraduation] =
    useState<GraduationResponseDTO | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  // IDs já carregados (resolvidos ou tentativa registrada) — evita re-fetch
  // em loop quando uma tag/cliente não pode ser resolvida.
  const tagsAttemptedRef = useRef<Set<number>>(new Set());
  const clientsAttemptedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const required = new Set<number>();
    list.data.forEach((graduation) =>
      graduation.tagIds.forEach((id) => required.add(id)),
    );
    const missing = Array.from(required).filter(
      (id) => !tagsAttemptedRef.current.has(id),
    );
    if (missing.length === 0) return;

    missing.forEach((id) => tagsAttemptedRef.current.add(id));

    let cancelled = false;
    Promise.all(
      missing.map((id) => tagAdminService.getById(id).catch(() => null)),
    ).then((results) => {
      if (cancelled) return;
      const resolved = results.filter(
        (tag): tag is TagResponseDTO => tag !== null,
      );
      if (resolved.length === 0) return;
      setTagsLookup((prev) => {
        const next = new Map(prev);
        resolved.forEach((tag) => next.set(tag.id, tag));
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [list.data]);

  useEffect(() => {
    const required = new Set<number>();
    list.data.forEach((graduation) => {
      if (graduation.clientId) required.add(graduation.clientId);
    });
    const missing = Array.from(required).filter(
      (id) => !clientsAttemptedRef.current.has(id),
    );
    if (missing.length === 0) return;

    missing.forEach((id) => clientsAttemptedRef.current.add(id));

    let cancelled = false;
    Promise.all(
      missing.map((id) => clientAdminService.getById(id).catch(() => null)),
    ).then((results) => {
      if (cancelled) return;
      const resolved = results.filter(
        (c): c is ClientResponseDTO => c !== null,
      );
      if (resolved.length === 0) return;
      setClientsLookup((prev) => {
        const next = new Map(prev);
        resolved.forEach((c) => next.set(c.id, c));
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [list.data]);

  const openDialog = useCallback(
    (mode: DialogMode, graduation: GraduationResponseDTO | null) => {
      setSelectedGraduation(graduation);
      setDialogMode(mode);
      setDialogContent(mode);
    },
    [],
  );

  const closeDialog = useCallback(() => {
    setDialogMode(null);
  }, []);

  const handleApplyFilters = useCallback(
    (filters: GraduationListFilters) => {
      list.setFilters(filters);
    },
    [list],
  );

  const handleClearFilters = useCallback(() => {
    list.setFilters({});
  }, [list]);

  const buildRequestPayload = (
    data: GraduationFormData,
  ): GraduationRequestDTO => ({
    eventName: data.eventName,
    eventDate: new Date(data.eventDate).toISOString(),
    totalValue: data.totalValue,
    paymentMethod: data.paymentMethod,
    description: data.description?.trim() ? data.description.trim() : null,
    ...(data.invoiceObservation?.trim()
      ? { invoiceObservation: data.invoiceObservation.trim() }
      : {}),
    sellerIds: data.sellerIds,
    tagIds: data.tagIds,
    skipInvoiceEmission: data.skipInvoiceEmission,
    clientId: data.clientId ?? null,
  });

  const handleFormSubmit = async (data: GraduationFormData) => {
    setDialogLoading(true);
    try {
      const payload = buildRequestPayload(data);
      if (dialogMode === DIALOG_MODE.CREATE) {
        const created = await graduationService.create(payload);
        list.prependLocal(created);
        toast.success("Formatura criada");
      } else if (dialogMode === DIALOG_MODE.EDIT && selectedGraduation) {
        const updated = await graduationService.update(
          selectedGraduation.idGraduation,
          payload,
        );
        list.replaceLocal(updated);
        toast.success("Formatura atualizada");
      }
      closeDialog();
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedGraduation) return;
    setDialogLoading(true);
    try {
      await graduationService.remove(selectedGraduation.idGraduation);
      list.removeLocal(selectedGraduation.idGraduation);
      toast.success("Formatura excluída");
      closeDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao excluir formatura"));
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!selectedGraduation) return;
    setDialogLoading(true);
    try {
      await graduationService.cancel(selectedGraduation.idGraduation);
      list.removeLocal(selectedGraduation.idGraduation);
      toast.success("Formatura cancelada — NF em processamento");
      closeDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao cancelar formatura"));
    } finally {
      setDialogLoading(false);
    }
  };

  const formMode = useMemo(() => {
    if (dialogContent === DIALOG_MODE.CREATE) return "create" as const;
    return "edit" as const;
  }, [dialogContent]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Formaturas</h1>
          <p className="text-muted-foreground">
            Eventos de formatura com vendedores e tags associados.
          </p>
        </div>
        <div className="flex gap-2 self-start md:self-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={list.reload}
            disabled={list.loading}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
          <Can permission={ACTIONS.graduations.create}>
            <Button
              size="sm"
              onClick={() => openDialog(DIALOG_MODE.CREATE, null)}
              disabled={list.loading}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Nova Formatura
            </Button>
          </Can>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-6">
          <GraduationFilters
            filters={list.filters}
            loading={list.loading}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
          />

          {list.loading && list.data.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <Spinner className="w-6 h-6" />
            </div>
          ) : list.error && list.data.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <p className="text-destructive">{list.error}</p>
              <Button variant="outline" size="sm" onClick={list.reload}>
                Tentar novamente
              </Button>
            </div>
          ) : list.data.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              Nenhuma formatura encontrada para os filtros aplicados.
            </div>
          ) : (
            <GraduationsTable
              graduations={list.data}
              tagsLookup={tagsLookup}
              clientsLookup={clientsLookup}
              loading={list.loading}
              currentPage={list.page}
              totalPages={list.totalPages}
              onEdit={(graduation) => openDialog(DIALOG_MODE.EDIT, graduation)}
              onCancel={(graduation) =>
                openDialog(DIALOG_MODE.CANCEL, graduation)
              }
              onDelete={(graduation) =>
                openDialog(DIALOG_MODE.DELETE, graduation)
              }
              onPageChange={list.setPage}
            />
          )}
        </CardContent>
      </Card>

      {(dialogContent === DIALOG_MODE.CREATE ||
        dialogContent === DIALOG_MODE.EDIT) && (
        <GraduationFormDialog
          open={
            dialogMode === DIALOG_MODE.CREATE || dialogMode === DIALOG_MODE.EDIT
          }
          mode={formMode}
          loading={dialogLoading}
          graduation={selectedGraduation}
          onClose={closeDialog}
          onSubmit={handleFormSubmit}
        />
      )}
      {dialogContent === DIALOG_MODE.DELETE && (
        <GraduationDeleteDialog
          open={dialogMode === DIALOG_MODE.DELETE}
          loading={dialogLoading}
          graduation={selectedGraduation}
          onClose={closeDialog}
          onConfirm={handleDeleteConfirm}
        />
      )}
      {dialogContent === DIALOG_MODE.CANCEL && (
        <GraduationCancelDialog
          open={dialogMode === DIALOG_MODE.CANCEL}
          loading={dialogLoading}
          graduation={selectedGraduation}
          onClose={closeDialog}
          onConfirm={handleCancelConfirm}
        />
      )}
    </div>
  );
}
